// AE „App statt Website“ (contract docs/AD-AE-MENUBAR.md §7, 02.11.2026) – the reference for every suite desktop app.
// Desktop only: the shared device id (Rust `device_id`, file RimoCore/device-id), a heartbeat to RimoID every 60 s through
// the BFF (`core/suite/presence`, gone after 150 s, `DELETE` when the window closes), and RimoID's event stream
// (`core/suite/events`): `suite.focus` for RimoCompta on this device brings the window to the front and follows its link
// (`document=<id>` → the document in RimoPreview). The suite switcher uses `deviceId()` to mark programs running here
// („In der App öffnen“) and `focusApp` to bring them to the front instead of opening them anew.
import { invoke } from '@tauri-apps/api/core';
import { getVersion } from '@tauri-apps/api/app';
import { api, API_URL, clientHeaders, refreshSession, tokens } from '../api/client';
import { openFile } from '../components/docFiles';
import { isDesktop } from '../platform';

let device: string | null = null;
const HEARTBEAT_MS = 60_000;

/** The device id once read (null in the browser or before `startPresence`). */
export const deviceId = () => device;

/** One SSE frame → {event, data}; comments (`: keepalive`) give null (pure, for tests). */
export function parseFrame(frame: string): { event: string; data: string } | null {
  let event = 'message'; const data: string[] = [];
  for (const line of frame.split('\n')) {
    if (!line || line.startsWith(':')) continue;
    const i = line.indexOf(':'), k = i < 0 ? line : line.slice(0, i), v = i < 0 ? '' : line.slice(i + 1).replace(/^ /, '');
    if (k === 'event') event = v; else if (k === 'data') data.push(v);
  }
  return data.length || event !== 'message' ? { event, data: data.join('\n') } : null;
}
/** A `suite.focus` for this app and device → the link to follow ('' = just come to the front), else null (pure). */
export function focusFor(ev: { event: string; data: string }, mine: string | null): string | null {
  if (ev.event !== 'suite.focus' || !mine) return null;
  try {
    const d = JSON.parse(ev.data) as { app?: string; deviceId?: string; link?: string | null };
    return d.app === 'rimocompta' && d.deviceId === mine ? d.link ?? '' : null;
  } catch { return null; }
}

/** Follows a focus: the main window to the front, then the link (only the catalog's key `document`). */
async function onFocus(link: string) {
  await invoke('tray_show_main').catch(() => {});
  const m = /^document=([0-9a-f-]{36})$/i.exec(link);
  if (m) await openFile(m[1]!, 'Dokument');
}

/** Reads the stream until it ends (token expired, network); then the caller reconnects with a fresh token. */
async function listen(signal: AbortSignal) {
  const res = await fetch(API_URL + '/core/suite/events', { headers: { ...clientHeaders(), Accept: 'text/event-stream', ...(tokens.access ? { Authorization: 'Bearer ' + tokens.access } : {}) }, signal });
  if (res.status === 401) { await refreshSession().catch(() => false); return; }
  if (!res.ok || !res.body) throw new Error('events ' + res.status);
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    buf += value.replace(/\r\n/g, '\n');
    let i;
    while ((i = buf.indexOf('\n\n')) >= 0) {
      const ev = parseFrame(buf.slice(0, i)); buf = buf.slice(i + 2);
      if (!ev) continue;
      if (ev.event === 'token_expired') { await refreshSession().catch(() => false); return; }
      const link = focusFor(ev, device);
      if (link !== null) void onFocus(link);
    }
  }
}

/** Starts heartbeat and stream once signed in (desktop only); returns the stop function. */
export function startPresence(): () => void {
  if (!isDesktop()) return () => {};
  let stopped = false, beat: ReturnType<typeof setInterval> | undefined;
  const ctl = new AbortController();
  const send = (version: string) => { if (tokens.access && device) void api('/core/suite/presence', { method: 'POST', body: { deviceId: device, version } }).catch(() => {}); };
  const gone = () => { if (device && tokens.access) void fetch(API_URL + '/core/suite/presence', { method: 'DELETE', keepalive: true, headers: { ...clientHeaders(), 'Content-Type': 'application/json', Authorization: 'Bearer ' + tokens.access }, body: JSON.stringify({ deviceId: device }) }).catch(() => {}); };
  (async () => {
    try { device = await invoke<string>('device_id'); } catch { return; } // no device id: no presence (the app works as before)
    const version = await getVersion().catch(() => '');
    send(version);
    beat = setInterval(() => send(version), HEARTBEAT_MS);
    window.addEventListener('beforeunload', gone);
    // The stream: reconnect after its end (token expired, RimoID restarted) with a short pause, longer after errors.
    let pause = 2_000;
    while (!stopped) {
      try { if (tokens.access) { await listen(ctl.signal); pause = 2_000; } } catch { pause = Math.min(pause * 2, 60_000); }
      if (stopped) break;
      await new Promise((r) => setTimeout(r, pause));
    }
  })();
  return () => { stopped = true; ctl.abort(); if (beat) clearInterval(beat); window.removeEventListener('beforeunload', gone); gone(); };
}

/** The switcher: bring another program's desktop app on this device to the front (RimoID suite/focus); false = not running. */
export async function focusApp(app: string, link?: string): Promise<boolean> {
  if (!device) return false;
  try { const r = await api<{ delivered?: boolean }>('/core/suite/focus', { method: 'POST', body: { app, deviceId: device, ...(link ? { link } : {}) } }); return r?.delivered !== false; }
  catch { return false; }
}
